import {
  definitionModule,
  estModuleAddon,
  PALIERS_MODULE,
  palierVerrouillable,
  type Module,
  type ModuleOptionnel,
  type PalierModule,
} from '@artosera/shared';
import type { GalerieSession, SessionUtilisateur } from '../../api/types.js';
import {
  galerieALaction,
  moduleEnLectureSeule,
  modulesAVenirDeLaSession,
  modulesDeLaSession,
} from '../../auth/sujet.js';
import { ENTREES_NAVIGATION } from '../shell/navigation.js';
import { APPUIS, destinationDuModule, familleDuModule, type FamilleCarte } from './catalogue.js';

/** Un palier de déblocage encore verrouillable : tout sauf le MVP. */
export type PalierAVenir = Exclude<PalierModule, 'mvp'>;

/**
 * Ce que la régie montre des modules à venir, tiré de la seule session : aucun
 * appel réseau, jamais. `aucun` quand aucun palier n'est verrouillé — alors ni
 * section, ni case fantôme, ni pastille, ni carte.
 */
export interface ModulesAVenir {
  /** Les modules à venir, par palier puis dans l'ordre du registre. */
  readonly modules: readonly ModuleOptionnel[];
  /** Les paliers qui en ont, dans l'ordre des paliers. */
  readonly paliers: readonly PalierAVenir[];
  readonly aucun: boolean;
}

export function modulesAVenirDe(
  session: Pick<SessionUtilisateur, 'modulesVerrouilles'>,
  galerie: GalerieSession,
): ModulesAVenir {
  const modules = modulesAVenirDeLaSession(session, galerie);
  const paliers = PALIERS_MODULE.filter(
    (palier): palier is PalierAVenir =>
      palierVerrouillable(palier) && modules.some((module) => palierDuModule(module) === palier),
  );
  return { modules, paliers, aucun: modules.length === 0 };
}

/** Le palier de déblocage d'un module complémentaire ou d'un service. */
export function palierDuModule(module: Module): PalierModule {
  const definition = definitionModule(module);
  return definition.famille === 'addon' ? 'mvp' : definition.palier;
}

/** Les modules d'un palier qui sont à venir, dans l'ordre du registre. */
export function modulesAVenirDuPalier(
  avenir: ModulesAVenir,
  palier: PalierModule,
): readonly ModuleOptionnel[] {
  return avenir.modules.filter((module) => palierDuModule(module) === palier);
}

/** Les modules d'un palier, hors celui-ci : « Arrive avec lui en Vx ». */
export function avecLuiAuMemePalier(
  avenir: ModulesAVenir,
  module: ModuleOptionnel,
): readonly ModuleOptionnel[] {
  return modulesAVenirDuPalier(avenir, palierDuModule(module)).filter((autre) => autre !== module);
}

/**
 * Les modules sur lesquels s'appuie un module à venir ET que la galerie a
 * d'effectif : les dépendances du registre et la liste éditoriale. Un module
 * lui-même à venir ou absent n'y figure pas.
 */
export function appuisActifs(module: ModuleOptionnel, galerie: GalerieSession): readonly Module[] {
  const effectifs = new Set(modulesDeLaSession(galerie));
  const candidats = [...definitionModule(module).dependances, ...APPUIS[module]];
  return [...new Set(candidats)].filter((candidat) => effectifs.has(candidat));
}

// ── La carte ────────────────────────────────────────────────────────────────

export type FiltreCarte = 'tout' | 'actifs' | PalierAVenir;

export interface TuileCarte {
  readonly module: Module;
  readonly famille: FamilleCarte;
  /** `true` : module à venir (verrouillé, sans accès) ; `false` : actif. */
  readonly avenir: boolean;
  /** Le palier d'un module à venir ; `null` pour un module actif. */
  readonly palier: PalierAVenir | null;
  /** Module consultable mais en lecture seule : actif à l'ouverture, marqué d'un cadenas. */
  readonly lectureSeule: boolean;
}

/**
 * Les tuiles de la carte : les modules ACTIFS de la galerie (add-ons exclus),
 * puis les modules à venir. Un module d'un palier débloqué que la galerie n'a
 * pas souscrit n'y est pas : il reste invisible.
 */
export function tuilesDeLaCarte(
  galerie: GalerieSession,
  avenir: ModulesAVenir,
): readonly TuileCarte[] {
  const actifs: TuileCarte[] = modulesDeLaSession(galerie)
    .filter((module) => !estModuleAddon(module))
    .map((module) => ({
      module,
      famille: familleDuModule(module),
      avenir: false,
      palier: null,
      lectureSeule: moduleEnLectureSeule(galerie, module),
    }));
  const aVenir: TuileCarte[] = avenir.modules.map((module) => ({
    module,
    famille: familleDuModule(module),
    avenir: true,
    palier: palierDuModule(module) as PalierAVenir,
    lectureSeule: false,
  }));
  return [...actifs, ...aVenir];
}

export function tuileVisible(tuile: TuileCarte, filtre: FiltreCarte): boolean {
  if (filtre === 'tout') return true;
  if (filtre === 'actifs') return !tuile.avenir;
  return tuile.avenir && tuile.palier === filtre;
}

/** Le palier que la carte ouvre d'abord : le prochain à venir. */
export function filtreParDefaut(avenir: ModulesAVenir): FiltreCarte {
  return avenir.paliers[0] ?? 'tout';
}

/**
 * Le filtre réellement appliqué : le choix de la personne tant qu'il existe
 * encore, sinon le défaut. Un palier qui n'a plus de module à venir (débloqué
 * page ouverte) ne laisse jamais la carte vide sous un filtre qui n'existe plus.
 */
export function filtreEffectif(choisi: FiltreCarte | null, avenir: ModulesAVenir): FiltreCarte {
  if (choisi === 'tout' || choisi === 'actifs') return choisi;
  if (choisi !== null && avenir.paliers.includes(choisi)) return choisi;
  return filtreParDefaut(avenir);
}

/**
 * Où mène un module ACTIF : sa page, si la galerie a le droit d'y entrer, sinon
 * `null` (la tuile se montre alors sans lien). Même garde que la navigation.
 */
export function lienDuModuleActif(
  galerie: GalerieSession,
  galerieSlug: string,
  module: Module,
): string | null {
  const destination = destinationDuModule(module);
  if (destination === null) return null;
  const entree = ENTREES_NAVIGATION.find((candidate) => candidate.chemin === destination);
  if (entree && !galerieALaction(galerie, entree.action)) return null;
  return destination === '' ? `/${galerieSlug}` : `/${galerieSlug}/${destination}`;
}
